import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProfileWrapper } from '@/shared/components/CardWrapper'
import { CardContent } from "@/shared/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select"
import TemplateEditor from '@/features/profile/templates/ProfileTemplateEditor';
import { CreateTemplateDialog } from '@/features/profile/templates/CreateTemplateDialog'
import { TemplateList } from '@/features/profile/templates/TemplateList'
import { isTemplateGenerating } from '@/features/profile/templates/templateStatus'
import { useGetToolsQuery } from "@/api/rtk/toolsApi"
import { useGetReportTemplatesQuery } from "@/api/rtk/reportsApi"
import { toolsToExcludeFromSelection } from "@/shared/config/constants"

const TEMPLATE_GENERATION_POLL_MS = 10_000;
const ALL_TOOLS = "all";

const Templates = () => {
  const { t } = useTranslation();
  const { data: toolsData } = useGetToolsQuery();
  const tools = (toolsData?.tools || []).filter(
    (tool) => !toolsToExcludeFromSelection.includes(tool.name)
  );
  const { data: templatesData, refetch: refetchTemplates } = useGetReportTemplatesQuery();
  const userTemplates = templatesData?.user_templates || [];

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [activeTab, setActiveTab] = useState("personal");
  const [toolFilter, setToolFilter] = useState(ALL_TOOLS);

  const hasGeneratingTemplates = useMemo(
    () => userTemplates.some((template) => isTemplateGenerating(template)),
    [userTemplates]
  );

  useEffect(() => {
    if (!hasGeneratingTemplates || selectedTemplateId) return undefined;

    const intervalId = window.setInterval(() => {
      refetchTemplates();
    }, TEMPLATE_GENERATION_POLL_MS);

    return () => window.clearInterval(intervalId);
  }, [refetchTemplates, hasGeneratingTemplates, selectedTemplateId]);

  const filteredTemplates = useMemo(() => {
    if (toolFilter === ALL_TOOLS) return userTemplates;
    return userTemplates.filter((ut) => String(ut?.tool_id) === String(toolFilter));
  }, [userTemplates, toolFilter]);

  const personal = useMemo(
    () => filteredTemplates.filter((ut) => ut?.owner_type !== "org"),
    [filteredTemplates]
  );
  const organisation = useMemo(
    () => filteredTemplates.filter((ut) => ut?.owner_type === "org"),
    [filteredTemplates]
  );

  const toolsById = useMemo(() => {
    const map = new Map();
    for (const t of tools) map.set(t.id, t);
    return map;
  }, [tools]);

  const selectedTemplate = useMemo(
    () => userTemplates.find((ut) => String(ut.id) === String(selectedTemplateId)) || null,
    [userTemplates, selectedTemplateId]
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    setActiveTab(selectedTemplate?.owner_type === "org" ? "org" : "personal");
  }, [selectedTemplate?.id]);

  const buttonContent = (
    <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
      <Select value={toolFilter} onValueChange={setToolFilter}>
        <SelectTrigger className="w-full sm:w-[180px] focus:ring-0 focus:ring-offset-0">
          <SelectValue placeholder={t("profile.templates.filter.allTools")} />
        </SelectTrigger>
        <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
          <SelectItem value={ALL_TOOLS}>
            {t("profile.templates.filter.allTools")}
          </SelectItem>
          {tools.map((tool) => (
            <SelectItem key={tool.id} value={String(tool.id)}>
              {tool.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <CreateTemplateDialog
        tools={tools}
        onCreated={(owner_type) => {
          setActiveTab(owner_type === "org" ? "org" : "personal");
        }}
      />
    </div>
  );

  return (
    <ProfileWrapper
      title={t("profile.tabs.templates")}
      description={t("profile.templates.description")}
      hasButton
      buttonContent={buttonContent}
      hideHeader={!!selectedTemplateId}
    >
      <CardContent className="p-0 w-full">
        {selectedTemplate ? (
          <TemplateEditor
            template={selectedTemplate}
            tools={tools}
            onBack={() => setSelectedTemplateId(null)}
            onSaved={({ owner_type }) => {
              if (owner_type) setActiveTab(owner_type === "org" ? "org" : "personal");
              setSelectedTemplateId(null);
            }}
          />
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="personal" className="w-full justify-center">
                {t("profile.templates.tabs.personal")}
              </TabsTrigger>
              <TabsTrigger value="org" className="w-full justify-center">
                {t("profile.templates.tabs.organisation")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="personal" className="mt-4">
              <TemplateList
                list={personal}
                emptyKey="profile.templates.empty.personal"
                toolsById={toolsById}
                onSelect={setSelectedTemplateId}
              />
            </TabsContent>
            <TabsContent value="org" className="mt-4">
              <TemplateList
                list={organisation}
                emptyKey="profile.templates.empty.organisation"
                toolsById={toolsById}
                onSelect={setSelectedTemplateId}
              />
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </ProfileWrapper>
  );
};

export default Templates;
